// PlanForm with Tabs - Refactored
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SubscriptionPlan } from '@/hooks/usePlans';
import { PricingFields } from './PricingFields';
import { BasicMetricsSelector } from './BasicMetricsSelector';
import { IntegrationsConfig } from './IntegrationsConfig';
import { AdvancedMetricsConfig } from './AdvancedMetricsConfig';
import { GoalsConfig } from './GoalsConfig';
import { ImportConfig } from './ImportConfig';
import { Info, DollarSign, BarChart3, Link2, Zap, CreditCard } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface PlanFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (plan: Partial<SubscriptionPlan>) => void;
  initialData?: SubscriptionPlan | null;
  isLoading?: boolean;
}

export function PlanForm({ open, onClose, onSubmit, initialData, isLoading }: PlanFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priceMonthly, setPriceMonthly] = useState(0);
  const [priceAnnual, setPriceAnnual] = useState(0);
  const [priceLifetime, setPriceLifetime] = useState(0);
  const [stripeProductId, setStripeProductId] = useState('');
  const [stripePriceMonthlyId, setStripePriceMonthlyId] = useState('');
  const [stripePriceAnnualId, setStripePriceAnnualId] = useState('');
  const [stripePriceLifetimeId, setStripePriceLifetimeId] = useState('');
  const [maxUsers, setMaxUsers] = useState(1);
  const [maxProducts, setMaxProducts] = useState(0);
  const [basicMetrics, setBasicMetrics] = useState<string[]>([]);
  
  // Integrações
  const [integrationsMode, setIntegrationsMode] = useState<'predefined' | 'customizable'>('predefined');
  const [maxIntegrations, setMaxIntegrations] = useState(0);
  const [allowedIntegrations, setAllowedIntegrations] = useState<string[]>([]);
  
  // Métricas Avançadas
  const [advancedMetricsEnabled, setAdvancedMetricsEnabled] = useState(false);
  const [advancedMetricsMode, setAdvancedMetricsMode] = useState<'predefined' | 'customizable'>('predefined');
  const [advancedMetrics, setAdvancedMetrics] = useState<string[]>([]);
  const [maxAdvancedMetrics, setMaxAdvancedMetrics] = useState(0);
  
  // Metas
  const [goalsEnabled, setGoalsEnabled] = useState(false);
  const [maxGoals, setMaxGoals] = useState(0);
  
  // Importação
  const [manualEntryEnabled, setManualEntryEnabled] = useState(true);
  const [bulkImportEnabled, setBulkImportEnabled] = useState(false);
  const [prioritySupport, setPrioritySupport] = useState(false);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description || '');
      setPriceMonthly(initialData.price_monthly || 0);
      setPriceAnnual(initialData.price_annual || 0);
      setPriceLifetime(initialData.price_lifetime || 0);
      setStripeProductId(initialData.stripe_product_id || '');
      setStripePriceMonthlyId(initialData.stripe_price_monthly_id || '');
      setStripePriceAnnualId(initialData.stripe_price_annual_id || '');
      setStripePriceLifetimeId(initialData.stripe_price_lifetime_id || '');
      setMaxUsers(initialData.max_users);
      setMaxProducts((initialData as any).max_products || 0);
      setBasicMetrics(initialData.basic_metrics || []);
      setIntegrationsMode(initialData.integrations_mode || 'predefined');
      setMaxIntegrations(initialData.max_integrations);
      setAllowedIntegrations(initialData.allowed_integrations || []);
      setAdvancedMetricsEnabled(initialData.advanced_metrics_enabled || false);
      setAdvancedMetricsMode(initialData.advanced_metrics_mode || 'predefined');
      setAdvancedMetrics(initialData.advanced_metrics || []);
      setMaxAdvancedMetrics(initialData.max_advanced_metrics || 0);
      setGoalsEnabled(initialData.goals_enabled || false);
      setMaxGoals(initialData.max_goals || 0);
      setManualEntryEnabled(initialData.manual_entry_enabled ?? true);
      setBulkImportEnabled(initialData.bulk_import_enabled || false);
      setPrioritySupport(initialData.priority_support || false);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...(initialData?.id && { id: initialData.id }),
      name,
      description: description || null,
      price_monthly: priceMonthly,
      price_annual: priceAnnual,
      price_lifetime: priceLifetime,
      stripe_product_id: stripeProductId || null,
      stripe_price_monthly_id: stripePriceMonthlyId || null,
      stripe_price_annual_id: stripePriceAnnualId || null,
      stripe_price_lifetime_id: stripePriceLifetimeId || null,
      max_users: maxUsers,
      max_products: maxProducts,
      basic_metrics: basicMetrics,
      integrations_mode: integrationsMode,
      max_integrations: maxIntegrations,
      allowed_integrations: allowedIntegrations,
      advanced_metrics_enabled: advancedMetricsEnabled,
      advanced_metrics_mode: advancedMetricsMode,
      advanced_metrics: advancedMetrics,
      max_advanced_metrics: maxAdvancedMetrics,
      goals_enabled: goalsEnabled,
      max_goals: maxGoals,
      manual_entry_enabled: manualEntryEnabled,
      bulk_import_enabled: bulkImportEnabled,
      priority_support: prioritySupport,
      is_active: true,
    } as any);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{initialData ? 'Editar Plano' : 'Novo Plano'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <Tabs defaultValue="basic" className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-5 mb-4">
              <TabsTrigger value="basic" className="flex items-center gap-2">
                <Info className="h-4 w-4" />
                <span className="hidden sm:inline">Básico</span>
              </TabsTrigger>
              <TabsTrigger value="metrics" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">Métricas</span>
              </TabsTrigger>
              <TabsTrigger value="integrations" className="flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                <span className="hidden sm:inline">Integrações</span>
              </TabsTrigger>
              <TabsTrigger value="advanced" className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                <span className="hidden sm:inline">Avançado</span>
              </TabsTrigger>
              <TabsTrigger value="stripe" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                <span className="hidden sm:inline">Stripe</span>
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto pr-2">
              <TabsContent value="basic" className="space-y-6 mt-0">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Plano</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: Free, Básico, Pro, Enterprise"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição do Plano (opcional)</Label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva as características deste plano..."
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    rows={3}
                  />
                </div>

                <PricingFields
                  priceMonthly={priceMonthly}
                  priceAnnual={priceAnnual}
                  priceLifetime={priceLifetime}
                  onPriceMonthlyChange={setPriceMonthly}
                  onPriceAnnualChange={setPriceAnnual}
                  onPriceLifetimeChange={setPriceLifetime}
                />

                <div className="space-y-2">
                  <Label htmlFor="maxUsers">Máximo de Usuários</Label>
                  <Input
                    id="maxUsers"
                    type="number"
                    min="1"
                    value={maxUsers}
                    onChange={(e) => setMaxUsers(parseInt(e.target.value))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxProducts">Máximo de Produtos (0 = ilimitado)</Label>
                  <Input
                    id="maxProducts"
                    type="number"
                    min="0"
                    value={maxProducts}
                    onChange={(e) => setMaxProducts(parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Configure 0 para produtos ilimitados
                  </p>
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label htmlFor="prioritySupport" className="text-sm">
                      Suporte Prioritário
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Atendimento com prioridade
                    </p>
                  </div>
                  <Switch
                    id="prioritySupport"
                    checked={prioritySupport}
                    onCheckedChange={setPrioritySupport}
                  />
                </div>
              </TabsContent>

              <TabsContent value="metrics" className="space-y-6 mt-0">
                <BasicMetricsSelector
                  selected={basicMetrics}
                  onChange={setBasicMetrics}
                />
              </TabsContent>

              <TabsContent value="integrations" className="space-y-6 mt-0">
                <IntegrationsConfig
                  mode={integrationsMode}
                  maxIntegrations={maxIntegrations}
                  allowedIntegrations={allowedIntegrations}
                  onModeChange={setIntegrationsMode}
                  onMaxIntegrationsChange={setMaxIntegrations}
                  onAllowedIntegrationsChange={setAllowedIntegrations}
                />
              </TabsContent>

              <TabsContent value="advanced" className="space-y-6 mt-0">
                <AdvancedMetricsConfig
                  enabled={advancedMetricsEnabled}
                  mode={advancedMetricsMode}
                  maxMetrics={maxAdvancedMetrics}
                  selectedMetrics={advancedMetrics}
                  onEnabledChange={setAdvancedMetricsEnabled}
                  onModeChange={setAdvancedMetricsMode}
                  onMaxMetricsChange={setMaxAdvancedMetrics}
                  onSelectedMetricsChange={setAdvancedMetrics}
                />

                <GoalsConfig
                  enabled={goalsEnabled}
                  maxGoals={maxGoals}
                  onEnabledChange={setGoalsEnabled}
                  onMaxGoalsChange={setMaxGoals}
                />

                <ImportConfig
                  manualEntryEnabled={manualEntryEnabled}
                  bulkImportEnabled={bulkImportEnabled}
                  onManualEntryChange={setManualEntryEnabled}
                  onBulkImportChange={setBulkImportEnabled}
                />
              </TabsContent>

              <TabsContent value="stripe" className="space-y-6 mt-0">
                <Alert>
                  <AlertDescription>
                    Configure os IDs do Stripe para habilitar pagamentos. Deixe em branco se não usar Stripe neste plano.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="stripeProductId">Product ID do Stripe</Label>
                  <Input
                    id="stripeProductId"
                    value={stripeProductId}
                    onChange={(e) => setStripeProductId(e.target.value)}
                    placeholder="prod_xxxxxxxxxxxxx"
                  />
                  <p className="text-xs text-muted-foreground">
                    ID do produto no Stripe (começa com prod_)
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="stripePriceMonthlyId">Price ID Mensal</Label>
                    <Input
                      id="stripePriceMonthlyId"
                      value={stripePriceMonthlyId}
                      onChange={(e) => setStripePriceMonthlyId(e.target.value)}
                      placeholder="price_xxxxxxxxxxxxx"
                    />
                    <p className="text-xs text-muted-foreground">
                      {priceMonthly === 0 ? 'Preço mensal é R$ 0' : 'Assinatura recorrente mensal'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="stripePriceAnnualId">Price ID Anual</Label>
                    <Input
                      id="stripePriceAnnualId"
                      value={stripePriceAnnualId}
                      onChange={(e) => setStripePriceAnnualId(e.target.value)}
                      placeholder="price_xxxxxxxxxxxxx"
                    />
                    <p className="text-xs text-muted-foreground">
                      {priceAnnual === 0 ? 'Preço anual é R$ 0' : 'Assinatura recorrente anual'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="stripePriceLifetimeId">Price ID Vitalício</Label>
                    <Input
                      id="stripePriceLifetimeId"
                      value={stripePriceLifetimeId}
                      onChange={(e) => setStripePriceLifetimeId(e.target.value)}
                      placeholder="price_xxxxxxxxxxxxx"
                    />
                    <p className="text-xs text-muted-foreground">
                      {priceLifetime === 0 ? 'Preço vitalício é R$ 0' : 'Pagamento único (one-time)'}
                    </p>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {initialData ? 'Salvar Alterações' : 'Criar Plano'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
